import { useMemo } from "react";
import { useCallback } from "react";
import styled from "styled-components";
import { ArrowRightIcon, TrashIcon } from "@heroicons/react/24/outline";
import { Action, Button } from "@sablier/v2-components/molecules";
import { tags } from "@sablier/v2-constants";
import { usePolledDate } from "@sablier/v2-hooks";
import { useT } from "@sablier/v2-locales";
import { _ } from "@sablier/v2-mixins";
import { useModalMomentPicker } from "~/client/hooks";

const WrapperPartial = styled.div`
  ${(props) => props.theme.styles.row}
  & {
    width: 100%;
    padding: calc(${(props) => props.theme.sizes.edge} * 1)
      ${(props) => props.theme.sizes.modalEdge};
    border-top: 2px solid ${(props) => props.theme.colors.border};
  }
`;

const Actions = styled.div`
  ${(props) => props.theme.styles.row}
  & {
    column-gap: calc(${(props) => props.theme.sizes.edge} * 1 / 2);
  }
`;

const Wrapper = styled(WrapperPartial)`
  ${(props) => props.theme.medias.maxMD} {
    padding: calc(${(props) => props.theme.sizes.edge} * 1)
      calc(${(props) => props.theme.sizes.edge} * 3 / 2);
  }
  ${(props) => props.theme.medias.maxSM} {
    padding: calc(${(props) => props.theme.sizes.edge} * 3 / 4);
    *[data-component="action"] {
      & > *:first-child {
        display: none;
      }
      & > *:last-child {
        width: 100%;
      }
    }
    ${Actions} {
      width: 100%;
      & > * {
        flex: 1;
      }
    }
  }
`;

interface Props {
  moment: [string | undefined, (value?: string) => void];
}

function Footer({ moment }: Props) {
  const { data, doClose } = useModalMomentPicker();
  const [value] = moment;
  const { t } = useT();

  const onConfirm = useCallback(() => {
    if (_.isFunction(data.onConfirm)) {
      data.onConfirm(value ? _.toString(value) : undefined);
    }
    doClose();
  }, [data, doClose, value]);

  const onClear = useCallback(() => {
    if (_.isFunction(data.onConfirm)) {
      data.onConfirm(undefined);
    }
    doClose();
  }, [data, doClose]);

  const now = usePolledDate({ difference: 0, frequency: 30 * 1000 });
  const preview = useMemo(() => _.toDuration(now, "date-estimate")[0], [now]);

  return (
    <Wrapper>
      <Action title={t("modalCards.moment.hint")} description={preview}>
        <Actions>
          <Button
            accent={"gray400"}
            appearance={"transparent"}
            onClick={onClear}
            right={TrashIcon}
            title={t("modalCards.moment.clear")}
            titleShort={t("words.clear")}
          />
          <Button
            accent={"iconic400"}
            appearance={"outline"}
            onClick={onConfirm}
            right={ArrowRightIcon}
            title={t("modalCards.moment.confirm")}
            titleShort={t("words.confirm")}
            cy={tags.actionMomentPicker}
          />
        </Actions>
      </Action>
    </Wrapper>
  );
}

export default Footer;
